/**
 * useClickstream(id, dims) — one-liner instrumentation for a page/panel:
 *   - View on mount
 *   - Dwell per visible stretch, flushed when the page is hidden and on departure, so the
 *     duration survives a tab that is terminated while hidden
 *   - Abandon on a real departure (browser/tab close, navigation, iframe teardown, unmount)
 *     without markComplete(); Complete when markComplete() was called. Timed on total
 *     engaged time, excluding stretches spent in the background
 *   - trackInteraction(control) / trackFailure(control) for per-control events
 *
 * markComplete() records Complete synchronously (iframe teardown / standalone re-mount make
 * deferring to unmount unreliable) and is idempotent.
 */

import {useCallback, useEffect, useMemo, useRef} from 'react';
import {Clickstream, ComponentId, ClickstreamDimensions} from 'src/app/util/Clickstream';

export interface UseClickstream {
    trackInteraction: (control: string, dims?: ClickstreamDimensions) => void;
    trackFailure: (control: string, dims?: ClickstreamDimensions) => void;
    markComplete: () => void;
}

/** dims: low-cardinality only (enforcement, mode) — never PII / reviewId.
 *  abandonable: false when completion is structurally impossible, so the unmount is not
 *  counted as a drop-off. */
export function useClickstream(id: ComponentId, dims?: ClickstreamDimensions, ready = true,
                               abandonable = true): UseClickstream {
    const completed = useRef<boolean>(false);
    const departed = useRef<boolean>(false);
    const visibleSince = useRef<number | null>(null);
    const engagedMs = useRef<number>(0);
    const abandonableRef = useRef<boolean>(abandonable);
    // Latest dims in a ref so the unmount cleanup reads current values without re-running
    // the effect (which would reset the dwell clock on every dims change).
    const dimsRef = useRef<ClickstreamDimensions | undefined>(dims);
    dimsRef.current = dims;
    abandonableRef.current = abandonable;

    const flushDwell = useCallback((emitEmpty: boolean) => {
        if (visibleSince.current === null) {
            return;
        }
        const segmentMs = Date.now() - visibleSince.current;
        visibleSince.current = null;
        engagedMs.current += segmentMs;
        if (segmentMs > 0 || emitEmpty) {
            Clickstream.recordDwell(id, segmentMs, dimsRef.current);
        }
    }, [id]);

    const depart = useCallback(() => {
        flushDwell(true);
        if (completed.current || departed.current) {
            return;
        }
        departed.current = true;
        if (abandonableRef.current) {
            Clickstream.recordAbandon(id, engagedMs.current, dimsRef.current);
        }
    }, [id, flushDwell]);

    const onVisibilityChange = useCallback(() => {
        if (document.visibilityState === 'hidden') {
            flushDwell(false);
        } else if (!departed.current && !completed.current) {
            visibleSince.current = Date.now();
        }
    }, [flushDwell]);

    useEffect(() => {
        if (!ready) {
            return;
        }
        // Reset per id/ready so each started surface gets its own start time and funnel state.
        completed.current = false;
        departed.current = false;
        engagedMs.current = 0;
        visibleSince.current = document.visibilityState === 'hidden' ? null : Date.now();
        Clickstream.recordView(id, dimsRef.current);
        window.addEventListener('beforeunload', depart);
        window.addEventListener('pagehide', depart);
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            window.removeEventListener('beforeunload', depart);
            window.removeEventListener('pagehide', depart);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            depart();
        };
    }, [id, ready, depart, onVisibilityChange]);

    const trackInteraction = useCallback((control: string, extraDims?: ClickstreamDimensions) => {
        Clickstream.recordInteraction(`${id}.${control}`, {...dimsRef.current, ...extraDims});
    }, [id]);

    const trackFailure = useCallback((control: string, extraDims?: ClickstreamDimensions) => {
        Clickstream.recordFailure(`${id}.${control}`, {...dimsRef.current, ...extraDims});
    }, [id]);

    const markComplete = useCallback(() => {
        if (completed.current) {
            return;
        }
        flushDwell(true);
        completed.current = true;
        Clickstream.recordComplete(id, engagedMs.current, dimsRef.current);
    }, [id, flushDwell]);

    return useMemo(() => ({trackInteraction, trackFailure, markComplete}),
        [trackInteraction, trackFailure, markComplete]);
}

export default useClickstream;
